import React, { ReactNode, useState } from "react"
import {
  Box, Heading, Stack,
} from "@chakra-ui/react"
import { useHistory } from "react-router-dom"
import Spinner from "components/elements/Spinner"
import { useChecklistTick, useCurrentEvent } from "queries/events"
import { useAccountConfiguration } from "queries/users"
import usePlans from "queries/plans"
import useCurrentAccount from "services/useCurrentAccount"
import useFeatureFlag from "services/featureFlags"
import useFeatures from "services/useFeatures"
import useModal from "services/useModal"
import usePermissions, { EventUserRole } from "services/permissions"
import { AccountUser, ModalName } from "sharedTypes"
import { isTerminalPlan } from "utilities/objects"
import BuyTextMessageCredits from "modals/TextMessage/TextMessageCredits/BuyTextMessageCredits"
import Modal from "components/dialogs/Modal"
import {
  eventBadgeTemplateEditPath,
  eventFormsPath,
  eventSeatingPath,
  eventSettingsPath,
  eventWebPagePath,
} from "utilities/routes"
import { useTextDeliveryCounts } from "queries/texts"
import { useForms } from "queries/forms"
import EventImage from "../Dashboard/components/EventImage"
import Header from "./components/Header"
import Activity from "./components/Activity"
import { PANEL_WIDTH } from "./components/CustomizePanel"
import {
  AttendanceCard, EmailsCard, GuestsCard, SeatingCard, TicketingCard,
} from "./cards/StatCards"
import ChecklistCard from "./cards/ChecklistCard"
import checklistSteps from "./checklistSteps"
import PageViewsCard from "./cards/PageViewsCard"
import QuickActionsCard from "./cards/QuickActionsCard"
import QuestionsCard from "./cards/QuestionsCard"
import SubmissionsCard from "./cards/SubmissionsCard"
import TextsCard from "./cards/TextsCard"
import UsageCard, { creditsRow, creditsUsed } from "./cards/UsageCard"
import EventDetails from "./components/EventDetails"
import { BlockId } from "./layout"
import { eventPhase, useMinuteClock } from "./phase"
import quickActionGroups from "./quickActions"

type Props = {
  accountUsers: AccountUser[]
}

const NewDashboard = ({ accountUsers }: Props) => {
  const currentAccount = useCurrentAccount()
  const { data: event, isLoading: eventIsLoading } = useCurrentEvent()
  const { plans, isLoading: plansAreLoading } = usePlans()
  const showModal = useModal()
  const history = useHistory()
  const features = useFeatures()
  const {
    texting: textingEnabled,
    badgeBuilder: badgeBuilderEnabled,
    vanityUrls: vanityUrlsEnabled,
    walletPass: walletPassEnabled,
  } = useFeatureFlag()
  const {
    canViewGuests,
    canViewEmails,
    canViewSeating,
    canViewSettings,
    canViewWebsite,
    canViewForm,
    canViewSubmissions,
    canViewTexting,
    canViewBadges,
  } = usePermissions()
  const now = useMinuteClock()
  const [customizing, setCustomizing] = useState(false)
  const [buyingCredits, setBuyingCredits] = useState(false)
  const [uploadingImage, setUploadingImage] = useState(false)
  const { accountConfiguration: { smsCreditPackages } } = useAccountConfiguration()
  const { data: deliveryCounts, isLoading: deliveryCountsLoading } = useTextDeliveryCounts(
    event?.id ?? "",
    { enabled: Boolean(event?.id) && textingEnabled && canViewTexting },
  )

  const tickChecklist = useChecklistTick(event?.id ?? "")
  const { data: forms, isError: formsFailed } = useForms(event?.id ?? "", { enabled: canViewSettings })

  if (eventIsLoading || plansAreLoading) {
    return <Spinner />
  }

  if (!event?.id) {
    return null
  }

  const { id: eventId, counts, website } = event
  const creditsUsedHere = deliveryCounts && creditsUsed(deliveryCounts)
  const { subscription, dailyEmailLimit, smsCredits } = currentAccount
  const phase = eventPhase(event.startDate, event.endDate, event.timezone, now)
  const shareLink = () => showModal(vanityUrlsEnabled ? ModalName.EventLink : ModalName.WebPageURL)
  const uploadImage = () => setUploadingImage(true)
  const isDoorStaff = event.currentUserRole === EventUserRole.DoorStaff
  const texting = textingEnabled && canViewTexting
  const teamSize = accountUsers
    ?.filter(({ admin, events }) => admin || events.find(({ event: { id } }) => id === eventId))
    .length

  const { steps, optionalSteps, loading: checklistLoading } = checklistSteps({
    event,
    phase,
    forms: forms?.data,
    formsFailed,
    vanityUrlsEnabled,
    texting,
    walletPassEnabled,
    badgesEnabled: badgeBuilderEnabled && canViewBadges,
    shareLink,
    uploadImage,
    showModal,
  })

  const quickActions = quickActionGroups({
    canViewGuests,
    isDoorStaff,
    canViewEmails,
    texting,
    canViewSettings,
    checkInLocationsEnabled: event.checkInLocationsEnabled,
    badgeBuilderEnabled,
    canViewBadges,
    showModal,
    editBadge: (badgeId) => history.push(eventBadgeTemplateEditPath(eventId, badgeId)),
  })

  const missingFeatures = Object.entries({
    "Badge Printing": features.badgePrinting,
    "Waitlist Forms": features.waitlistForms,
    "White-Label": features.whiteLabel,
    "Custom Email Domains": features.customEmailDomains,
    "Tables & Seating": features.tablesAndSeating,
    "Custom Filters": features.customFilters,
  }).filter(([, enabled]) => !enabled).map(([name]) => name)

  const blocks: Partial<Record<BlockId, ReactNode>> = {
    checklist: canViewSettings && (
      <ChecklistCard
        phase={phase}
        steps={steps}
        optionalSteps={optionalSteps}
        loading={checklistLoading}
        ticks={event.checklistTicks}
        onTick={tickChecklist}
      />
    ),
    views: (
      <PageViewsCard
        event={event}
        canEdit={canViewWebsite}
        onPublish={() => history.push(eventWebPagePath(eventId))}
        onShareLink={shareLink}
      />
    ),
    quick: (
      <QuickActionsCard
        coverImageUrl={website.coverImageUrl}
        canEditImage={canViewWebsite}
        onUploadImage={uploadImage}
        groups={quickActions}
      />
    ),
    guests: canViewGuests && (
      <GuestsCard event={event} onEmptyAction={() => showModal(ModalName.ImportGuests)} />
    ),
    attendance: canViewGuests && (
      <AttendanceCard event={event} />
    ),
    emails: canViewEmails && (
      <EmailsCard event={event} onEmptyAction={() => showModal(ModalName.NewEmail)} />
    ),
    texts: texting && (
      <TextsCard eventId={eventId} />
    ),
    seating: canViewSeating && (
      <SeatingCard event={event} onEmptyAction={() => history.push(eventSeatingPath(eventId))} />
    ),
    ticketing: canViewForm && (
      <TicketingCard
        event={event}
        onEmptyAction={() => history.push(eventFormsPath(eventId))}
        onConnectStripe={canViewSettings
          ? () => history.push(eventSettingsPath(eventId, "ticketing-payments"))
          : undefined}
      />
    ),
    questions: canViewSubmissions && (
      <QuestionsCard eventId={eventId} onSetUp={() => showModal(ModalName.QuestionForm)} />
    ),
    submissions: canViewSubmissions && (
      <SubmissionsCard
        eventId={eventId}
        currency={event.currency}
        timeZone={event.timezone}
      />
    ),
    usage: canViewSettings && (
      <UsageCard
        rows={[
          ...event.nonTicketedGuestLimit ? [{
            label: "Guest Limit", used: counts.guests, limit: event.nonTicketedGuestLimit, unit: "guests",
          }] : [],
          {
            label: "Daily Email Limit", used: counts.dailyEmails, limit: dailyEmailLimit, unit: "emails",
          },
          ...textingEnabled ? [creditsRow({
            used: creditsUsedHere,
            left: smsCredits ?? 0,
            loading: canViewTexting && deliveryCountsLoading,
            onBuy: smsCreditPackages && (() => setBuyingCredits(true)),
          })] : [],
          ...teamSize ? [{
            label: "Team Size", used: teamSize, limit: event.maxTeamSize, unit: "seats",
          }] : [],
        ]}
        planName={subscription?.type}
        missingFeatures={missingFeatures}
        canUpgrade={!isTerminalPlan(plans, subscription)}
        onUpgrade={() => showModal(ModalName.Upgrade)}
      />
    ),
  }

  return (
    <Box
      minH="100vh"
      flexShrink={0}
      bg="gray.100"
      fontSize="sm"
      // Below xl the panel covers the cards instead, which leaves them more room.
      pr={customizing ? { base: 0, xl: PANEL_WIDTH } : 0}
    >
      <Stack p={12} maxW="1480px" mx="auto" spacing={0} data-testid="dashboard-main-content">
        <Header event={event} phase={phase} now={now} />
        <Heading fontSize="2xl" fontWeight="semibold" letterSpacing="tight" mt={8} mb={4}>Activity</Heading>
        {/* Dimmed and out of reach while the cards below are customized, as in the design. */}
        <Box
          opacity={customizing ? 0.5 : 1}
          transition="opacity 0.15s"
          data-testid="dashboard-activity"
          {...(customizing && { inert: "" })}
        >
          <Activity
            key={phase}
            event={event}
            defaultMetric={phase === "day-of" ? "checkins" : "registrations"}
            phase={phase}
          />
        </Box>
        <EventDetails
          eventId={eventId}
          layout={event.dashboardLayout}
          canCustomize={canViewSettings}
          blocks={blocks}
          onCustomizingChange={setCustomizing}
        />
      </Stack>
      {buyingCredits && smsCreditPackages && (
        <BuyTextMessageCredits
          eventId={eventId}
          smsCreditPackages={smsCreditPackages}
          onHide={() => setBuyingCredits(false)}
        />
      )}
      <Modal show={uploadingImage} hide={() => setUploadingImage(false)} title="Event Image" size="lg">
        <EventImage
          eventId={eventId}
          coverImageUrl={website.coverImageUrl}
          canEdit={canViewWebsite}
        />
      </Modal>
    </Box>
  )
}

export default NewDashboard
